import type {
  AttendanceConfig,
  AttendanceRecord,
  DeductionAnalysis,
  DeductionsSummary,
  LateComersReport,
  MonthlySummary,
  PaginatedResponse,
  Payment,
  PayrollDeductions,
  PunctualityReport,
  StaffPerformance,
  User,
  UsersPaginatedResponse,
} from '@/types';
import { buildQueryString } from '@/utils';
import api from './api';

export interface StaffFilters {
  search?: string;
  department?: string;
  status?: string;
  role?: string;
  page?: number;
  limit?: number;
}

export interface AttendanceFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  status?: string;
  lateCategory?: string;
  page?: number;
  limit?: number;
}

export const adminService = {
  async getStaffList(filters: StaffFilters = {}): Promise<UsersPaginatedResponse<User>> {
    const queryString = buildQueryString({
      search: filters.search,
      department: filters.department,
      status: filters.status,
      role: filters.role,
      page: filters.page,
      limit: filters.limit,
    });

    const response = await api.get<UsersPaginatedResponse<User>>(`/admin/staff?${queryString}`);
    return response.data;
  },

  async getStaffDetails(userId: string): Promise<User> {
    const response = await api.get<User>(`/admin/staff/${userId}`);
    return response.data;
  },

  async updateStaff(userId: string, data: Partial<User>): Promise<User> {
    const response = await api.put<User>(`/admin/staff/${userId}`, data);
    return response.data;
  },

  async updateAttendanceConfig(userId: string, config: Partial<AttendanceConfig>): Promise<User> {
    const response = await api.put<User>(`/admin/staff/${userId}/attendance-config`, config);
    return response.data;
  },

  async approveUser(userId: string): Promise<User> {
    const response = await api.post<User>(`/admin/users/${userId}/approve`);
    return response.data;
  },

  async rejectUser(userId: string): Promise<User> {
    const response = await api.post<User>(`/admin/users/${userId}/reject`);
    return response.data;
  },

  async activateStaff(userId: string): Promise<User> {
    const response = await api.post<User>(`/admin/staff/${userId}/activate`);
    return response.data;
  },

  async deactivateStaff(userId: string): Promise<User> {
    const response = await api.post<User>(`/admin/staff/${userId}/deactivate`);
    return response.data;
  },

  async suspendStaff(userId: string): Promise<User> {
    const response = await api.post<User>(`/admin/staff/${userId}/suspend`);
    return response.data;
  },

  async resetPassword(
    userId: string,
    data: { newPassword?: string; generateRandom?: boolean; forceChange?: boolean },
  ): Promise<{ success: boolean; newPassword?: string; message: string }> {
    const response = await api.post(`/admin/staff/${userId}/reset-password`, data);
    return response.data;
  },

  async generatePassword(
    userId: string,
    forceChange: boolean = true,
  ): Promise<{ success: boolean; newPassword: string; message: string }> {
    const response = await api.post(`/admin/staff/${userId}/generate-password`, { forceChange });
    return response.data;
  },

  async bulkApplyConfig(
    staffIds: string[],
    config: Partial<AttendanceConfig>,
  ): Promise<{ success: boolean; updated: number }> {
    const response = await api.post('/admin/attendance-config/bulk-apply', { staffIds, config });
    return response.data;
  },

  async getStats(): Promise<{
    total: number;
    active: number;
    pending: number;
    suspended: number;
    deactivated: number;
  }> {
    const response = await api.get('/admin/stats/overview');
    return response.data;
  },

  async getDepartments(): Promise<string[]> {
    const response = await api.get<string[]>('/admin/departments');
    return response.data;
  },

  async getPendingApprovals(): Promise<UsersPaginatedResponse<User>> {
    const response = await api.get<UsersPaginatedResponse<User>>('/admin/pending-approvals');
    return response.data;
  },

  async getAllAttendance(
    filters: AttendanceFilters = {},
  ): Promise<PaginatedResponse<AttendanceRecord>> {
    const queryString = buildQueryString({
      startDate: filters.startDate,
      endDate: filters.endDate,
      userId: filters.userId,
      status: filters.status,
      lateCategory: filters.lateCategory,
      page: filters.page,
      limit: filters.limit,
    });

    const response = await api.get<PaginatedResponse<AttendanceRecord>>(
      `/attendance/admin/all?${queryString}`,
    );
    return response.data;
  },

  async getAttendanceById(id: string): Promise<AttendanceRecord> {
    const response = await api.get<AttendanceRecord>(`/attendance/admin/${id}`);
    return response.data;
  },

  async flagAttendance(id: string, reason: string): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(`/attendance/admin/${id}/flag`, { reason });
    return response.data;
  },

  async addNote(id: string, note: string): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(`/attendance/admin/${id}/note`, { note });
    return response.data;
  },

  async overrideAttendance(
    id: string,
    data: {
      adjustedClockInTime?: string;
      removeDeductions?: string[];
      markOnTime?: boolean;
      justification: string;
    },
  ): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(`/attendance/admin/${id}/override`, data);
    return response.data;
  },

  async pardonDeduction(
    attendanceId: string,
    deductionId: string,
    pardonReason: string,
  ): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(
      `/attendance/admin/${attendanceId}/deductions/${deductionId}/pardon`,
      { pardonReason },
    );
    return response.data;
  },

  async revokePardon(attendanceId: string, deductionId: string): Promise<AttendanceRecord> {
    const response = await api.post<AttendanceRecord>(
      `/attendance/admin/${attendanceId}/deductions/${deductionId}/revoke-pardon`,
    );
    return response.data;
  },

  async getDeductionsSummary(year: number, month: number): Promise<DeductionsSummary> {
    const queryString = buildQueryString({ year, month });
    const response = await api.get<DeductionsSummary>(`/admin/deductions/summary?${queryString}`);
    return response.data;
  },

  async getStaffDeductions(
    userId: string,
    year: number,
    month: number,
  ): Promise<AttendanceRecord[]> {
    const queryString = buildQueryString({ year, month });
    const response = await api.get<AttendanceRecord[]>(
      `/admin/deductions/staff/${userId}?${queryString}`,
    );
    return response.data;
  },

  async getMonthlySummary(year: number, month: number): Promise<MonthlySummary> {
    const queryString = buildQueryString({ year, month });
    const response = await api.get<MonthlySummary>(`/admin/reports/monthly-summary?${queryString}`);
    return response.data;
  },

  async getStaffPerformance(
    userId: string,
    year: number,
    month: number,
  ): Promise<StaffPerformance> {
    const queryString = buildQueryString({ year, month });
    const response = await api.get<StaffPerformance>(
      `/admin/reports/staff-performance/${userId}?${queryString}`,
    );
    return response.data;
  },

  async getDeductionAnalysis(year: number, month: number): Promise<DeductionAnalysis> {
    const queryString = buildQueryString({ year, month });
    const response = await api.get<DeductionAnalysis>(
      `/admin/reports/deduction-analysis?${queryString}`,
    );
    return response.data;
  },

  async getPayrollDeductions(
    year: number,
    month: number,
    department?: string,
  ): Promise<PayrollDeductions> {
    const queryString = buildQueryString({
      year,
      month,
      department,
    });

    const response = await api.get<PayrollDeductions>(
      `/admin/reports/payroll-deductions?${queryString}`,
    );
    return response.data;
  },

  async getLateComers(year: number, month: number, threshold?: number): Promise<LateComersReport> {
    const queryString = buildQueryString({
      year,
      month,
      threshold,
    });

    const response = await api.get<LateComersReport>(`/admin/reports/late-comers?${queryString}`);
    return response.data;
  },

  async getPaymentsByMonth(year: number, month: number): Promise<Payment[]> {
    const queryString = buildQueryString({ year, month });
    const response = await api.get<Payment[]>(`/payments/month?${queryString}`);
    return response.data;
  },

  async markPaymentAsPaid(paymentId: string): Promise<Payment> {
    const response = await api.patch<Payment>(`/payments/${paymentId}/mark-paid`);
    return response.data;
  },

  async recomputePayment(paymentId: string): Promise<Payment> {
    const response = await api.patch<Payment>(`/payments/${paymentId}/recompute`);
    return response.data;
  },

  async getPunctualityData(year: number, month: number): Promise<PunctualityReport> {
    const response = await api.get<PunctualityReport>(
      `/attendance/admin/punctuality?year=${year}&month=${month}`,
    );
    return response.data;
  },
};

export default adminService;
